// src/components/auth/AuthCallback.js
// Handles OAuth callback using onAuthStateChange listener,
// Decouples navigation via state update to avoid potential timing issues.

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../backend/supabase'; // Adjust path if needed
import axiosInstance from '../../axiosInstance'; // Adjust path if needed
import { Loader2, AlertCircle } from 'lucide-react'; // Import desired icons

const AuthCallback = () => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('Processing authentication...');
    const [redirectPath, setRedirectPath] = useState(null); // State to trigger navigation
    const processed = useRef(false); // Flag to prevent processing multiple times

    // Effect 1: Handle the actual navigation when redirectPath state changes
    useEffect(() => {
        if (redirectPath) {
            console.log(`[AuthCallback] Navigation effect triggered. Navigating to: ${redirectPath}`);
            // Use replace to remove the callback URL from browser history
            navigate(redirectPath, { replace: true });
        }
        // This effect depends only on redirectPath and navigate
    }, [redirectPath, navigate]);

    // Effect 2: Set up Supabase listener and timeout on component mount
    useEffect(() => {
        // Set a timeout to prevent hanging indefinitely if Supabase event doesn't fire
        const timeoutId = setTimeout(() => {
            if (!processed.current) {
                processed.current = true; // Mark as processed to prevent late event handling
                console.error("[AuthCallback] Timeout waiting for SIGNED_IN event.");
                setError("Authentication timed out. Please try signing in again.");
                setMessage("Authentication failed (timeout).");
                setRedirectPath('/signup/login'); // Set path to navigate on timeout
            }
        }, 15000); // 15-second timeout

        console.log('[AuthCallback] Setting up onAuthStateChange listener...');
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log(`[AuthCallback] onAuthStateChange event received: ${event}`);

            // --- Primary Success Condition ---
            // Check for SIGNED_IN event, a valid session, user, *and* access token, and ensure it hasn't been processed yet.
            if (event === 'SIGNED_IN' && session?.user && session?.access_token && !processed.current) {
                processed.current = true; // Mark as processed IMMEDIATELY
                clearTimeout(timeoutId); // Clear the safety timeout
                subscription?.unsubscribe(); // Clean up listener early
                console.log(`[AuthCallback] SIGNED_IN processed for user: ${session.user.email}, ID: ${session.user.id}`);

                // Call function to determine redirect path and SET STATE, not navigate directly
                await determineAndSetRedirectPath(session.user, session.access_token);

            // --- Handle Sign-In Errors ---
            } else if ((event === 'SIGN_IN_ERROR' || event === 'AUTH_CODE_ERROR') && !processed.current) {
                processed.current = true;
                clearTimeout(timeoutId);
                subscription?.unsubscribe();
                console.error("[AuthCallback] Sign in error event received:", event, session?.error);
                setError(`Failed to complete sign-in: ${session?.error?.message || 'Unknown authentication error.'}`);
                setMessage("Authentication failed.");
                setRedirectPath('/signup/login'); // Set path to navigate on error
            }
            // Ignore other events (like USER_UPDATED, TOKEN_REFRESHED) or already processed callbacks
        });

        // Cleanup function for when the component unmounts
        return () => {
            console.log("[AuthCallback] Cleaning up: Clearing timeout and unsubscribing auth listener.");
            clearTimeout(timeoutId);
            subscription?.unsubscribe(); // Ensure listener is always unsubscribed
        };
        // Intentionally empty dependency array [] so listener is set up only once on mount.
    }, []); // Empty dependency array ensures this effect runs only once

    /**
     * Determines the redirect path based on profile status and sets the redirectPath state.
     * @param {object} authUser - The user object from Supabase Auth.
     * @param {string} accessToken - The access token obtained from the Supabase session.
     */
    const determineAndSetRedirectPath = async (authUser, accessToken) => {
        setMessage('Verifying profile status...');
        let finalPath = '/signup/login'; // Default redirect path on error
        let backendResponse; // To log status in finally block

        try {
            // Double-check token presence (should be guaranteed by calling condition)
            if (!accessToken) {
                console.error("[AuthCallback determineRedirect] Critical error: accessToken is missing!");
                throw new Error("Authentication failed: Missing necessary token.");
            }

            console.log(`[AuthCallback determineRedirect] Calling backend: /api/users/verify-profile for user ID: ${authUser.id}`);
            // Make the backend call using axiosInstance WITH the explicit Authorization header
            backendResponse = await axiosInstance.get('/api/users/verify-profile', {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            console.log('[AuthCallback determineRedirect] Backend response received. Status:', backendResponse.status);
            console.log('[AuthCallback determineRedirect] Backend data:', backendResponse.data);

            const profileStatus = backendResponse.data;

            // Validate the structure of the backend response
            if (!profileStatus || typeof profileStatus.exists !== 'boolean') {
                 console.error("[AuthCallback determineRedirect] Invalid response structure from backend:", profileStatus);
                 throw new Error("Invalid response format received from verification server.");
            }

            // --- Redirection Logic ---
            const intendedRole = localStorage.getItem('userRole'); // Get role selected *before* OAuth started
            // Clean up temporary data regardless of outcome now
            localStorage.removeItem('tempAuthUser');
            localStorage.removeItem('userRole');

            if (profileStatus.exists) {
                // Profile EXISTS in public.users table
                console.log(`[AuthCallback determineRedirect] Profile exists for ${authUser.email}. Approved: ${profileStatus.is_approved}, Type: ${profileStatus.user_type}`);
                if (profileStatus.is_approved) {
                    finalPath = '/dashboard'; // Navigate to the main dashboard
                    setMessage('Redirecting to dashboard...');
                } else {
                    // User exists but is not approved (likely an attorney pending verification)
                    finalPath = '/pending-approval'; // Navigate to the pending page
                    setMessage('Account pending approval. Redirecting...');
                }
            } else {
                // Profile DOES NOT EXIST in public.users table - Needs creation/completion
                 console.log(`[AuthCallback determineRedirect] Profile does not exist for ${authUser.email}. Redirecting based on intended role: ${intendedRole}`);
                 // Store essential auth user info temporarily for the next step (if needed by target page)
                 localStorage.setItem('tempAuthUser', JSON.stringify({
                     id: authUser.id, // Store ID
                     email: authUser.email,
                     user_metadata: authUser.user_metadata || {} // Include metadata
                 }));

                 if (intendedRole === 'attorney') {
                     // Attorney needs to complete the multi-step registration form
                     localStorage.setItem('userRole', 'attorney'); // Re-set role if RegistrationForm needs it
                     finalPath = '/signup/register'; // Navigate to registration form
                     setMessage('Profile not found. Redirecting to complete registration...');
                 } else if (intendedRole === 'client') {
                     // Client profile needs creation
                     console.warn("[AuthCallback determineRedirect] New client requires profile creation.");
                     setMessage('Finalizing client setup...');
                     // Redirect client appropriately (adjust as needed)
                     finalPath = '/home'; // Or '/dashboard' or '/client-setup'
                 } else {
                      // Role wasn't set or is invalid
                      console.error("[AuthCallback determineRedirect] Intended user role missing or invalid.");
                      throw new Error("User role information is missing or invalid after sign-in.");
                 }
            }

        } catch (error) {
             console.error('[AuthCallback] Error during determineRedirect (backend call or processing):', error);
             if (error.isAxiosError) { /* log details */ }
             setError(error.response?.data?.error || error.message || 'An unexpected error occurred.');
             setMessage("Authentication check failed.");
             finalPath = '/signup/login'; // Ensure redirect to login on error
             // Clear potentially corrupt temp data again
             localStorage.removeItem('tempAuthUser');
             localStorage.removeItem('userRole');
        } finally {
             console.log(`[AuthCallback] Setting redirect path state to: ${finalPath}`);
             setRedirectPath(finalPath); // <<< SET STATE TO TRIGGER NAVIGATION EFFECT
        }
    }; // End of determineAndSetRedirectPath

    // --- Render Component UI ---
    if (error) {
        // Error UI...
         return (
             <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                 <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full shadow-sm text-center">
                     <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                     <h2 className="text-lg font-semibold text-red-700 mb-2">Authentication Error</h2>
                     <p className="text-red-600 mb-4">{message}</p>
                     <p className="text-sm text-gray-600 mb-4 break-words">{error}</p>
                     <button
                         onClick={() => navigate('/signup/login')}
                         className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                     >
                         Return to Login
                     </button>
                 </div>
             </div>
         );
    }

    // Loading state UI...
    return (
         <div className="min-h-screen bg-[#C5C0BB] flex items-center justify-center">
             <div className="text-center">
                 <Loader2 className="inline-block animate-spin w-10 h-10 text-[#1A1A1A] mb-4" />
                 <div className="text-[#1A1A1A] text-xl font-medium">{message}</div>
             </div>
         </div>
    );
};

export default AuthCallback;